import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Globe2, Building2, User } from "lucide-react";
import api from "../lib/api";
import Navbar from "../components/Navbar";

const ICONS = { nations: Globe2, organisations: Building2, people: User };

export default function Indices() {
  const [indices, setIndices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/indices");
        setIndices(data.indices || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="App" data-testid="indices-page">
      <Navbar />
      <section className="border-b border-[var(--es-line)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <p className="font-ui text-xs tracking-[0.28em] uppercase text-[var(--es-blue)]">
            The Indices
          </p>
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl text-[var(--es-ink)] tracking-tight mt-3 leading-[1.02] max-w-4xl">
            A <em className="text-[var(--es-blue)]">cartography</em> of erudition — across nations, institutions and people.
          </h1>
          <p className="font-body text-[var(--es-ink-soft)] mt-6 max-w-2xl text-lg">
            Composite Erudition Scores compiled from public indices (HDI, GII, WGI, freedom indices)
            and biographical sources. Illustrative readings, not absolute verdicts.
          </p>
        </div>
      </section>

      {loading ? (
        <p className="p-12 font-ui text-xs uppercase tracking-widest text-[var(--es-ink-soft)]">
          Loading…
        </p>
      ) : (
        <section>
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20 grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--es-line)] border border-[var(--es-line)]">
            {indices.map((idx) => {
              const Icon = ICONS[idx.slug] || Globe2;
              return (
                <Link
                  to={`/indices/${idx.slug}`}
                  key={idx.slug}
                  className="bg-white p-8 md:p-10 hover:bg-[var(--es-blue-soft)] transition-colors group flex flex-col"
                  data-testid={`index-card-${idx.slug}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 flex items-center justify-center bg-[var(--es-blue-soft)] text-[var(--es-blue)]">
                      <Icon size={22} strokeWidth={1.5} />
                    </div>
                    <span className="font-ui text-xs tracking-widest uppercase text-[var(--es-ink-soft)]">
                      {idx.count} entries
                    </span>
                  </div>
                  <h2 className="font-display text-3xl text-[var(--es-ink)] mt-8">{idx.label}</h2>
                  <p className="font-body text-[var(--es-ink-soft)] mt-2 leading-relaxed">
                    {idx.blurb}
                  </p>

                  <ol className="mt-8 border-t border-[var(--es-line)]">
                    {idx.top.map((t, i) => (
                      <li
                        key={t.slug}
                        className="grid grid-cols-12 gap-3 items-baseline py-3 border-b border-[var(--es-line)]"
                      >
                        <span className="col-span-1 font-ui text-xs uppercase tracking-widest text-[var(--es-ink-soft)]">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="col-span-8 font-display text-lg text-[var(--es-ink)] truncate">
                          {t.name}
                        </span>
                        <span className="col-span-3 text-right font-display text-xl text-[var(--es-blue)]">
                          {t.overall}
                        </span>
                      </li>
                    ))}
                  </ol>

                  <span className="mt-6 inline-flex items-center gap-2 font-ui text-xs uppercase tracking-widest text-[var(--es-blue)] group-hover:gap-3 transition-all">
                    Explore the index{" "}
                    <ArrowUpRight size={14} strokeWidth={1.6} />
                  </span>
                </Link>
              );
            })}
          </div>

          <div className="max-w-7xl mx-auto px-6 md:px-12 pb-20">
            <p className="font-body text-xs italic text-[var(--es-ink-soft)] max-w-3xl">
              These composites are an editorial illustration of the framework — not an empirical
              ranking. They are designed to make the five-pillar lens vivid, not authoritative.
            </p>
          </div>
        </section>
      )}
    </div>
  );
}
